import dayjs from 'dayjs'
import type { FailSafeMeta, RecordSchema } from '~/types'
import {
	isValidJSON,
	encrypt,
	decryptString,
	extractQueryValue,
	isArrayMatch,
	isSomeObjectValuesEmpty,
	isSomeObjectValuesFilled,
	stringify,
	isDuplicate
} from '~/utils/functions'

export const useUtils = () => {
	const productCategoryType = (categoryName?: string, planName: string | number = '') => {
		let type = {
			isHealth: false,
			isAuto: false,
			isComprehensive: false,
			isLaptopCover: false,
			isTravel: false,
			isGadget: false,
			isThirdParty: false,
			isOthers: false
		}
		if (!categoryName) {
			return type
		}
		const category = String(categoryName).toLowerCase()
		const plan = String(planName).toLowerCase()

		type = {
			isHealth: category === ProductCategory.Health,
			isAuto: category === ProductCategory.Auto,
			isComprehensive: plan === String(ProductCodeNames.COMPREHENSIVE_AUTO),
			isLaptopCover: plan === String(ProductCodeNames.LAPTOP_COVER),
			isTravel:
				category === ProductCategory.Travel ||
				plan.includes(ProductNames.AIICO_TRAVEL) ||
				plan.includes(ProductNames.TRAVEL_ALLIANZ),
			isGadget: category === ProductCategory.Gadget,
			isThirdParty: plan === String(ProductCodeNames.THIRD_PARTY),
			isOthers: category === ProductCategory.Others
		}
		return type
	}
	const planLabel = (categoryName?: string) => {
		if (categoryName) {
			const response = productCategoryType(categoryName)
			if (response?.isAuto) {
				return 'vehicle'
			} else if (response?.isGadget) {
				return 'gadget'
			}
			return 'staff'
		}
		return 'staff'
	}

	const addProductsIconFill = (arg: string, plan_name?: string) => {
		let name = ''
		let dir = ''
		let pathClass = ''
		const p_name = String(plan_name ?? '')
			.replace(/ /g, '_')
			?.toLowerCase()
		if (productCategoryType(arg)?.isHealth) {
			name = 'heart'
			dir = 'fill'
			switch (plan_name) {
				case 'flexicare':
					pathClass = 'fill-blue-600'
					break
				case 'zencare':
					pathClass = 'fill-pink-600'
					break
				case 'zencare_plus':
					pathClass = 'fill-success-500'
					break
				default:
					pathClass = 'fill-purple-600'
					break
			}
		} else if (productCategoryType(arg, plan_name)?.isAuto) {
			dir = 'vehicles'
			switch (p_name) {
				case 'mini_comprehensive':
					name = 'purple-car'
					break
				case 'comprehensive_cover':
					name = 'pink-car'
					break
				case 'third_party_comprehensive':
					name = 'blue-car'
					break
				default:
					name = 'orange-car'
					break
			}
		} else if (productCategoryType(arg, plan_name)?.isGadget) {
			name = 'gadget-pc'
			dir = 'fill'
		} else if (productCategoryType(arg, plan_name)?.isTravel) {
			dir = 'vehicles'
			name = 'plane'
		} else if (productCategoryType(arg, plan_name)?.isOthers) {
			dir = 'vehicles'
			name = 'delivery-van'
		}
		return {
			name,
			dir,
			pathClass
		}
	}
	const addProductsIconLine = (arg: string, plan_name?: string) => {
		let name = ''
		let dir = ''

		if (productCategoryType(arg, plan_name)?.isHealth) {
			name = 'heart'
			dir = 'line'
		} else if (productCategoryType(arg, plan_name)?.isAuto) {
			name = 'gray-car'
			dir = 'vehicles'
		} else if (productCategoryType(arg, plan_name)?.isGadget) {
			name = 'gadget-pc'
			dir = 'line'
		} else if (productCategoryType(arg, plan_name)?.isTravel) {
			dir = 'vehicles'
			name = 'gray-plane'
		} else if (productCategoryType(arg, plan_name)?.isOthers) {
			dir = 'vehicles'
			name = 'gray-van'
		}
		return {
			name,
			dir
		}
	}
	const addCategoryIcon = (category: string, active_category: string) => {
		let name = ''
		const dir = category === active_category ? 'fill' : 'line'
		const categoryType = productCategoryType(category)
		let color = ''

		if (categoryType.isHealth) {
			name = 'heart'
			color = category === active_category ? '#1F92D0' : ''
		} else if (categoryType?.isAuto) {
			name = 'auto-bus'
		} else if (categoryType?.isGadget) {
			name = 'monitor-mobile'
		} else if (categoryType?.isTravel) {
			name = 'plane'
		}
		return {
			name,
			dir,
			color
		}
	}
	const urlQuery = computed(() => {
		return useRoute().query as Record<string, string>
	})

	const navigateDialog = async <T = unknown>(arg: DialogRouteResponse<T>) => {
		const r = encryptData(arg)
		await useRouter().replace({
			query: {
				...urlQuery.value,
				dialog: r
			}
		})
	}
	const routeQuery = (payload: { page: string; method?: string; parseForm?: string }, replace?: boolean) => {
		const { page, method, parseForm } = payload
		navigateTo({
			query: {
				page: page,
				form_method: method,
				form: parseForm
			},
			replace
		})
	}
	const countDownDate = (arg: string) => {
		if (!arg) {
			return 0
		}

		// Parse the expiry date string to a JavaScript Date object
		const expiryDate: Date = new Date(arg)

		// Get today's date
		const today: Date = new Date()

		// Calculate the days left
		return Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 3600 * 24))
	}
	const daysLeft = (arg: string) => {
		if (!arg) {
			return {}
		}

		// Parse the expiry date string to a JavaScript Date object
		const providedDate = dayjs(arg)
		providedDate.diff()
		const monthDiff = dayjs().diff(providedDate, 'months')
		const daysDiff = dayjs().diff(providedDate, 'days')

		// Calculate the days left
		return {
			monthDiff,
			daysDiff: daysDiff < 1 ? 0 : daysDiff
		}
	}
	const filterOption = (input: string, option: RecordSchema) => {
		return toLower(String(option.value)).indexOf(toLower(input)) >= 0
	}
	const monthByNumber = computed<Record<string, number>>(() => ({
		annual: 12,
		'bi-annual': 6,
		monthly: 1,
		quarterly: 3
	}))
	const userSource = computed(() => {
		let source = ''
		if (import.meta.client) {
			source = localStorage.getItem('channel_source') ?? ''
		}
		return source
	})
	const listAllMonthsInYear = computed(() => {
		const year = dayjs(new Date()).year()
		const months: {
			name: string
			abbreviation: string
			number: number
		}[] = []

		for (let month = 0; month < 12; month++) {
			const date = dayjs(`${year}-${month + 1}-01`)
			const monthName = date.format('MMM')
			months.push({
				abbreviation: monthName,
				name: date.format('MMMM'),
				number: month + 1
			})
		}

		return months
	})
	/**
	 * @link https://www.mycovergenius.com/
	 * */
	const baseUrl = computed(() => `${location.protocol}//${location.host}`)
	const convertPeriodToMonths = (arg?: string | number, useLetters = true) => {
		if (arg) {
			const period = monthByNumber.value[String(arg).toLowerCase()]

			const number = period || String(arg)
			const num = parseInt(String(number), 0)
			if (num === 1 && useLetters) {
				return 'monthly'
			} else if (num >= 1 && num <= 11) {
				return num + ' month' + (num > 1 ? 's' : '')
			} else if (num >= 12) {
				const years = Math.floor(num / 12)
				const months = num % 12
				if (months === 0) {
					return years + ' year' + (years > 1 ? 's' : '')
				} else {
					return (
						years + ' year' + (years > 1 ? 's' : '') + ' and ' + months + ' month' + (months > 1 ? 's' : '')
					)
				}
			}
		}
	}
	const constructMetaData = (
		event: 'BUY' | 'FUND_WALLET' | 'RENEW',
		payload: {
			user_id: string
			endpoint: string
			body?: object | null
		}
	): FailSafeMeta => {
		const { user_id, body, endpoint } = payload
		return {
			event,
			user_id,
			endpoint,
			body: body ?? {}
		}
	}

	const getStatus = (
		arg: PolicyResponse | null,
		has = [
			'is_terminated',
			'is_pending',
			'to_be_removed',
			'to_be_terminated',
			'is_deactivated',
			'is_expired',
			'is_paused',
			'to_be_deactivated'
		]
	) => {
		const emptyStatus = {
			is_terminated: false,
			is_pending: false,
			to_be_removed: false,
			to_be_terminated: false,
			is_deactivated: false,
			is_expired: false,
			to_be_deactivated: false,
			is_active: false
		}
		if (!arg) {
			return emptyStatus
		}
		const resultKeys = (arg && Object.keys(arg)) || []
		if (!resultKeys.some(item => has.includes(item))) {
			return emptyStatus
		}
		const {
			is_terminated,
			is_pending,
			to_be_removed,
			to_be_terminated,
			is_deactivated,
			is_expired,
			to_be_deactivated,
			active
		} = arg
		const status = (urlQuery.value?.status as string) || ''
		return {
			is_terminated,
			is_pending: status === 'pending' || is_pending,
			to_be_removed,
			to_be_terminated,
			is_deactivated,
			is_expired,
			to_be_deactivated,
			is_active: (!is_pending && !is_deactivated && !is_expired) || active
		}
	}
	const generateYearsList = (numYears = 10): number[] => {
		const currentYear = dayjs().year()
		const yearsList: number[] = []
		for (let i = currentYear - numYears + 1; i <= currentYear; i++) {
			yearsList.push(i)
		}
		return yearsList.reverse()
	}
	const toLower = (arg: string | number | null | undefined | boolean) => String(arg).toLowerCase()
	/**
	 * The idea on this is that instead of defining html element for everything with a different styles, you can use a special character to denote and it will move everything after that special into the html element your provided the prefix into
	 * @example
	 * replacePlaceHolder('Johnny is a boy $bchild', "<span class='font-bold'>$b1</span>", '$b')
	 **/
	const replacePlaceHolder = (text: string, placeholder = '<span>$1</span>', prefix = '$') => {
		const regex = new RegExp(`\\${prefix}(\\w+)`, 'g')
		return String(text).replace(regex, placeholder)
	}
	const customerLabel = (
		productCatgeory: string,
		productName: string,
		autoLabel: 'custodian' | 'vehicle' = 'custodian'
	) => {
		const category = productCategoryType(productCatgeory, productName)
		const labels: Record<string, boolean> = {
			staff: category?.isHealth || category?.isTravel || false,
			[autoLabel]: category?.isAuto || false,
			gadget: category?.isGadget || false
		}
		return Object.keys(labels).find(item => labels[item])
	}
	const downloadBlob = (blob: Blob, file: { name: string; extension: string }) => {
		if (blob) {
			try {
				const FILE = window.URL.createObjectURL(blob)
				const docUrl = document.createElement('a')
				docUrl.href = FILE
				docUrl.setAttribute('download', file.name + file.extension)
				document.body.appendChild(docUrl)
				docUrl.click()
				docUrl.remove()
				URL.revokeObjectURL(FILE)
			} catch (e) {
				console.error(e)
			}
		}
	}

	return {
		routeQuery,
		downloadBlob,
		constructMetaData,
		addCategoryIcon,
		userSource,
		isSomeObjectValuesEmpty,
		urlQuery,
		isValidJSON,
		addProductsIconFill,
		addProductsIconLine,
		isArrayMatch,
		decryptString,
		encrypt,
		productCategoryType,
		navigateDialog,
		monthByNumber,
		convertPeriodToMonths,
		getStatus,
		countDownDate,
		listAllMonthsInYear,
		generateYearsList,
		daysLeft,
		stringify,
		toLower,
		baseUrl,
		filterOption,
		extractQueryValue,
		replacePlaceHolder,
		customerLabel,
		isDuplicate,
		planLabel,
		isSomeObjectValuesFilled
	}
}
